import axios from 'axios';
import { getApp, initializeApp } from 'firebase/app';
import { getMessaging, getToken } from 'firebase/messaging';
import { v4 as uuidv4 } from 'uuid';

import ENVIRONMENT from '@/environments/environment';

import { storageService } from './storage';

const axiosInstance = axios.create({
  baseURL: ENVIRONMENT.NOTIFICATIONS_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

const DEFAULT_TAGS = [
  'ALL',
  'Android',
  'Cordova',
  'DEBUG',
  'discounts',
  'newCampaigns',
  'newProducts',
  'offers',
];

export enum WebNotificationStatus {
  'GRANTED' = 'GRANTED',
  'DENIED' = 'DENIED',
  'NOT_ESTABLISHED' = 'NOT_ESTABLISHED',
}

export enum WebNotificationPermission {
  'GRANTED' = 'GRANTED',
  'DENIED' = 'DENIED',
  'PROMPT' = 'PROMPT',
}

const setWebNotificationStatus = (status: WebNotificationStatus) => localStorage.setItem('webNotificationStatus', status);

export const getWebNotificationStatus = () => {
  const status = localStorage.getItem('webNotificationStatus');
  if (!status) {
    localStorage.setItem(
      'webNotificationStatus',
      WebNotificationStatus.NOT_ESTABLISHED,
    );
    return WebNotificationStatus.NOT_ESTABLISHED;
  }
  return status;
};

const registrationWebRequest = async ({
  tags = DEFAULT_TAGS,
  pushEnabled = true,
} = {}) => {
  const URL = ENVIRONMENT.URL_API_NOTIFICATION
    + ENVIRONMENT.URL_API_NOTIFICATION_APIS.REGISTRATION;
  const deviceToken = storageService.getFcmToken();

  let subscriberKey = localStorage.getItem('username');
  if (subscriberKey) {
    subscriberKey = subscriberKey.substring(3); // Remove first three characters
  }

  if (!deviceToken || !subscriberKey) {
    return;
  }

  let deviceId = localStorage.getItem('deviceId');
  if (!deviceId) {
    deviceId = uuidv4();
    localStorage.setItem('deviceId', deviceId);
  }

  const body = {
    deviceID: deviceId,
    device_Token: deviceToken,
    sdk_Version: '8.0.0',
    app_Version: '1.0.0 : 1',
    dST: true,
    location_Enabled: false,
    proximity_Enabled: false,
    platform_Version: '13',
    push_Enabled: pushEnabled,
    timeZone: '7200',
    subscriberKey,
    platform: 'Android',
    hwid: 'Web Application',
    etAppId: ENVIRONMENT.SFMC_APP_ID,
    locale: 'es_ES',
    tags,
    attributes: [],
    registrationDateUtc: new Date().toISOString(),
    quietPushEnabled: false,
    registrationId: deviceId,
  };
  const response = await axiosInstance.post(URL, body);

  return response;
};

const deregisterWebDevice = async () => {
  registrationWebRequest({ pushEnabled: false });
  localStorage.removeItem('fcmToken');
};

const initializeWebNotifications = () => {
  const firebaseConfig = {
    apiKey: ENVIRONMENT.FIREBASE_API_KEY,
    authDomain: ENVIRONMENT.FIREBASE_AUTH_DOMAIN,
    projectId: ENVIRONMENT.FIREBASE_PROJECT_ID,
    storageBucket: ENVIRONMENT.FIREBASE_STORAGE_BUCKET,
    messagingSenderId: ENVIRONMENT.FIREBASE_MESSAGING_SENDER_ID,
    appId: ENVIRONMENT.FIREBASE_APP_ID,
    measurementId: ENVIRONMENT.FIREBASE_MEASUREMENT_ID,
  };

  const app = initializeApp(firebaseConfig);
  const messaging = getMessaging(app);

  getToken(messaging, { vapidKey: ENVIRONMENT.VAPID_KEY })
    .then((token) => {
      localStorage.setItem('fcmToken', token);
      registrationWebRequest();
    })
    .catch((error) => {
      console.log('An error occurred while retrieving token. ', error);
    });

  return messaging;
};

// eslint-disable-next-line no-async-promise-executor
const checkWebInAppMessages = () => new Promise(async (resolve, reject) => {
  const databases = await indexedDB.databases();
  const existsInApp = databases.find((d) => d.name === 'InAppNotifications');

  if (existsInApp) {
    const inApp = indexedDB.open('InAppNotifications', 1);
    inApp.onsuccess = (event: Event) => {
      if (event && event.target) {
        const db = (event.target as IDBRequest).result as IDBDatabase;
        try {
          let transaction = db.transaction('Notifications', 'readwrite');
          let objectStore = transaction.objectStore('Notifications');
          const request: IDBRequest = objectStore.getAll();

          request.onsuccess = (messagesEvent: Event) => {
            const messages = (messagesEvent.target as IDBRequest).result;
            if (messages?.length > 0) {
              retrieveWebInAppMessages().then((response) => {
                if (response) {
                  const { nodes } = response.data;
                  const inAppMessages = nodes.find((n: unknown) => {
                    if (typeof n === 'object' && n !== null && 'name' in n) {
                      return n.name === 'inAppMessages';
                    }
                    return false;
                  });
                  console.log('Notifications:', inAppMessages);
                  const currentMessage = inAppMessages?.items?.at(0);
                  transaction = db.transaction('Notifications', 'readwrite');
                  objectStore = transaction.objectStore('Notifications');
                  objectStore.clear();
                  resolve(currentMessage);
                }
              });
            }
          };
        } catch (error) {
          console.log(error);
        }
      }
    };
    inApp.onerror = (event: Event) => {
      if (event && event.target && 'errorCode' in event.target) {
        reject(event.target.errorCode);
      }
    };
  }
});

const retrieveWebInAppMessages = async () => {
  const deviceId = localStorage.getItem('deviceId');

  if (!deviceId) {
    return null;
  }

  let URL = ENVIRONMENT.URL_API_NOTIFICATION
    + ENVIRONMENT.URL_API_NOTIFICATION_APIS.RETRIEVE;
  URL = URL.replace('<deviceId>', deviceId);

  const response = await axiosInstance.post(URL, {});

  return response;
};

export {
  checkWebInAppMessages,
  deregisterWebDevice,
  initializeWebNotifications,
  registrationWebRequest,
  retrieveWebInAppMessages,
};
